/**
 * Shared reorder pricing pieces — kept in one place so the dashboard
 * Quick-Reorder modal and the Catalogue Library detail modal can never
 * drift out of sync. Both surfaces hit `/catalogue/{id}/reorder-quote`
 * via `catalogueApi.reorderQuote` with 300 ms debounce.
 *
 *  • `useReorderQuote(entryId, quantity)` — fires the lookup whenever
 *    the inputs change. Returns `{ quote, loading }`.
 *  • `<ReorderQuoteCard quote loading />` — renders the slate-50 price
 *    preview card (estimated total, unit price, breakdown table,
 *    warnings). Visually identical on both surfaces.
 */
import { useEffect, useState } from 'react';
import { catalogueApi } from '../lib/api';

export function useReorderQuote(entryId, quantity) {
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!entryId) {
            setQuote(null);
            return;
        }
        const qty = parseInt(quantity, 10);
        if (!qty || qty < 1) {
            setQuote(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const res = await catalogueApi.reorderQuote(entryId, qty);
                if (!cancelled) setQuote(res.data);
            } catch (e) {
                if (!cancelled) {
                    setQuote({
                        ok: false,
                        warning: e?.response?.data?.detail || 'Could not fetch quote',
                    });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [entryId, quantity]);

    return { quote, loading };
}

export function ReorderQuoteCard({ quote, loading }) {
    return (
        <div
            className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm"
            data-testid="reorder-quote-card"
        >
            {loading && (
                <div className="text-xs text-slate-500" data-testid="reorder-quote-loading">
                    Calculating price…
                </div>
            )}
            {!loading && quote && quote.ok && (
                <div className="space-y-1">
                    <div className="flex items-center justify-between">
                        <span className="text-slate-500">Estimated price</span>
                        <span
                            className="text-base font-semibold text-slate-900 tabular-nums"
                            data-testid="reorder-quote-total"
                        >
                            {Number(quote.total_local).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}{' '}
                            {quote.currency}
                        </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>Unit price</span>
                        <span className="tabular-nums">
                            {Number(quote.unit_price_local).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}{' '}
                            {quote.currency} / pcs
                        </span>
                    </div>
                    {quote.currency !== 'EUR' && (
                        <div className="text-[10px] text-slate-400">
                            Based on{' '}
                            {Number(quote.total_eur).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                            })}{' '}
                            EUR · rate {Number(quote.exchange_rate).toFixed(4)}
                        </div>
                    )}
                    {Array.isArray(quote.line_breakdown) && quote.line_breakdown.length > 0 && (
                        <details className="mt-2" data-testid="reorder-quote-breakdown">
                            <summary className="cursor-pointer text-[11px] text-slate-500 hover:text-slate-700 select-none">
                                Show breakdown ({quote.line_breakdown.length} SKU
                                {quote.line_breakdown.length === 1 ? '' : 's'})
                            </summary>
                            <table className="w-full mt-1 text-[11px]">
                                <tbody>
                                    {quote.line_breakdown.map((line) => (
                                        <tr key={line.sku} className="border-t border-slate-100">
                                            <td className="py-0.5 pr-2 text-slate-600">
                                                <span className="font-mono">{line.sku}</span>{' '}
                                                <span className="text-slate-400">{line.display_name}</span>
                                            </td>
                                            <td className="py-0.5 text-slate-500 text-right tabular-nums">
                                                {Number(line.unit_price_eur).toFixed(2)} × {line.qty}
                                            </td>
                                            <td className="py-0.5 pl-2 text-slate-700 text-right tabular-nums font-medium">
                                                {Number(line.line_total_eur).toFixed(2)} EUR
                                            </td>
                                        </tr>
                                    ))}
                                    {Number(quote.setup_total_eur) > 0 && (
                                        <tr className="border-t border-slate-100">
                                            <td className="py-0.5 pr-2 text-slate-500">Setup fees</td>
                                            <td></td>
                                            <td className="py-0.5 pl-2 text-slate-700 text-right tabular-nums">
                                                {Number(quote.setup_total_eur).toFixed(2)} EUR
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            {Array.isArray(quote.skipped_links) && quote.skipped_links.length > 0 && (
                                <p className="text-[10px] text-slate-400 mt-1">
                                    Excluded: {quote.skipped_links.join(', ')}
                                </p>
                            )}
                        </details>
                    )}
                </div>
            )}
            {!loading && quote && !quote.ok && (
                <div className="text-xs text-amber-700" data-testid="reorder-quote-warning">
                    ⚠ {quote.warning}
                </div>
            )}
            {!loading && !quote && (
                <div className="text-xs text-slate-400">
                    Enter a quantity to see the estimated price.
                </div>
            )}
        </div>
    );
}

export default ReorderQuoteCard;
